import { ArrowDownRight, ArrowUpRight, Download, ReceiptText } from "lucide-react";
import { useMemo, useState } from "react";
import type { DashboardData } from "../types";
import { entryText, formatCredits, formatDate, monthLabel, sourceLabelKey } from "../utils";
import Amount from "../components/Amount";
import { useT } from "../i18n";
import { useServiceText } from "../service-text";

/** Keys, not sentences — a module constant is built before a language exists. */
const kindLabels: Record<string, string> = {
  allocation: "kind.allocation",
  rollover: "kind.rollover",
  topup: "kind.topup",
  spend: "kind.spend",
  adjustment: "kind.adjustment",
  refund: "kind.refund",
};

export default function LedgerPage({ data }: { data: DashboardData }) {
  const t = useT();
  const st = useServiceText();
  const [kind, setKind] = useState("all");
  const entries = useMemo(() => kind === "all" ? data.entries : data.entries.filter((entry) => entry.kind === kind), [data.entries, kind]);

  return (
    <div className="page-stack">
      <section className="ledger-balance-strip">
        {/* Amount, not a composed string: a sign and a unit assembled around a
            number drift to the wrong side of it in Arabic. This strip is where that
            was first visible — "-18 cr" read "cr 18-". */}
        <div><span>{t("overview.monthlyAllocation")}</span><strong><Amount value={data.account.monthlyCredits} unit="cr" /></strong></div>
        <div><span>{t("overview.additionalCredits")}</span><strong><Amount value={data.balance.carriedIn + data.balance.topups} sign="plus" unit="cr" /></strong></div>
        <div><span>{t("overview.creditsSpent")}</span><strong><Amount value={data.balance.spent} sign="deducted" unit="cr" /></strong></div>
        <div className="accent"><span>{t("overview.availableCredits")}</span><strong><Amount value={data.balance.available} unit="cr" /></strong></div>
      </section>

      <section className="table-toolbar ledger-toolbar">
        <div className="segmented-control" aria-label={t("ledger.filters")}>
          {["all", "spend", "allocation", "rollover", "topup"].map((item) => (
            <button key={item} className={kind === item ? "active" : ""} onClick={() => setKind(item)}>
              {t(item === "all" ? "kind.all" : kindLabels[item])}
            </button>
          ))}
        </div>
        <a className="button button-secondary" href={`/api/export/ledger.csv?month=${data.month}`}>
          <Download size={17} /> {t("common.exportCsv")}
        </a>
      </section>

      <section className="panel ledger-panel">
        <div className="panel-header compact ledger-heading">
          <div><span className="section-eyebrow">{monthLabel(data.month)}</span><h3>{t("ledger.history")}</h3></div>
          <span>{t("ledger.entryCount", { n: entries.length })}</span>
        </div>
        {entries.length ? (
          <div className="responsive-table-wrap">
            <table className="data-table ledger-table">
              <thead><tr><th>{t("table.date")}</th><th>{t("table.transaction")}</th><th>{t("table.service")}</th><th>{t("table.recordedBy")}</th><th>{t("table.source")}</th><th>{t("table.credits")}</th></tr></thead>
              <tbody>
                {entries.map((entry) => {
                  const positive = entry.credits >= 0;
                  // System rows are our sentences; a manager's row is theirs.
                  const text = entryText(entry, t);
                  return (
                    <tr key={entry.id}>
                      <td data-label={t("table.date")}>{formatDate(entry.occurred_at)}</td>
                      <td data-label={t("table.transaction")}>
                        <div className={`transaction-icon ${positive ? "positive" : "negative"}`}>
                          {positive ? <ArrowDownRight size={17} /> : <ArrowUpRight size={17} />}
                        </div>
                        <div className="transaction-copy">
                          <strong dir={text.titleAuthored ? "auto" : undefined}>{text.title}</strong>
                          <span dir={text.noteAuthored ? "auto" : undefined}>{text.note}</span>
                        </div>
                      </td>
                      <td data-label={t("ledger.colService")}>{st.name(entry.service_id, entry.service_name) || t("ledger.accountCredits")}</td>
                      {/* "System" is the honest answer for monthly allocations and
                           carry-forward, which nobody keyed in — and it is a word we
                           wrote, so it is translated like any other word we wrote. */}
                      <td data-label={t("table.recordedBy")}>{entry.created_by_name || t("ledger.systemActor")}</td>
                      <td data-label={t("table.source")}>{t(sourceLabelKey(entry.source))}</td>
                      <td data-label={t("table.credits")}><strong className={positive ? "credit-positive" : "credit-negative"}>{positive ? "+" : ""}{formatCredits(entry.credits)}</strong></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state"><ReceiptText size={30} /><h4>{t("ledger.emptyTitle")}</h4><p>{t("ledger.emptyBody")}</p></div>
        )}
      </section>
    </div>
  );
}
